import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_get_actual_schedule, API_get_events } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { get_t_str_shortened } from "../../../../helpers/helpers";
import PrintShell, {
  SettingsGroup,
  OptionToggle,
  MultiSelect,
} from "./print/PrintShell";
import { roleColorHex } from "./print/roleColors";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const ALL_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];


function formatDate(date) {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function calendarRange(year, month, weekStart) {
  const firstDow = new Date(year, month, 1).getDay();
  const leading = (firstDow - weekStart + 7) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalDays = leading + daysInMonth - 1 >= 35 ? 42 : 35;
  const start = new Date(year, month, 1 - leading);
  return { start, totalDays };
}

export default function PrintModeMonthly(props) {
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideOutsideMonth, setHideOutsideMonth] = useState(true);
  const [showRoleColors, setShowRoleColors] = useState(true);
  const [showEvents, setShowEvents] = useState(true);

  const currentMonth = props.currentMonth;
  const currentYear = props.currentYear;
  const weekStart = props.businessWeekStartDayIndex || 0;
  const daysOfWeek = [...ALL_DAYS.slice(weekStart), ...ALL_DAYS.slice(0, weekStart)];
  const monthLabel = `${MONTHS[currentMonth]} ${currentYear}`;
  const storeName = props.storeNames?.find(
    (s) => s.id === Number(props.store_id)
  )?.store_name;

  useEffect(() => {
    if (!props.store_id) return;
    const source = axios.CancelToken.source();
    const { start, totalDays } = calendarRange(currentYear, currentMonth, weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + totalDays - 1);
    const qs = `${props.store_id}&date_start=${formatDate(start)}&date_end=${formatDate(end)}`;
    const config = { ...getDefaultAxiosConfig(), cancelToken: source.token };

    setLoading(true);
    Promise.all([
      axios.get(`${API_get_actual_schedule}${qs}`, config),
      axios.get(`${API_get_events}${qs}`, config),
    ])
      .then(([scheduleRes, eventsRes]) => {
        const list =
          scheduleRes.data?.redux?.payload?.actual_schedule_list ?? scheduleRes.data;
        setScheduleData(Array.isArray(list) ? list : []);
        setEventsData(Array.isArray(eventsRes.data) ? eventsRes.data : []);
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error("Error fetching monthly schedule:", err);
        setScheduleData([]);
        setEventsData([]);
        setLoading(false);
      });
    return () => source.cancel("Print view closed or month changed");
  }, [currentMonth, currentYear, props.store_id, weekStart]);

  useEffect(() => {
    if (props.selectedRolesForFiltering) {
      setSelectedRoleIds(props.selectedRolesForFiltering);
    }
  }, [props.selectedRolesForFiltering]);

  const { start, totalDays } = calendarRange(currentYear, currentMonth, weekStart);
  const days = [];
  const cursor = new Date(start);
  for (let i = 0; i < totalDays; i++) {
    const dateStr = formatDate(cursor);
    days.push({
      date: dateStr,
      dayNumber: cursor.getDate(),
      isCurrentMonth:
        cursor.getMonth() === currentMonth && cursor.getFullYear() === currentYear,
      shifts: scheduleData
        .filter((s) => s.date === dateStr && !s.other_store)
        .filter((s) => !selectedRoleIds.length || selectedRoleIds.includes(s.role_id))
        .sort((a, b) => a.shift_start - b.shift_start),
      events: eventsData.filter((e) => e.date === dateStr),
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  const roleOptions = (props.role_names || []).map((r) => ({
    value: r.id,
    label: r.role_name,
  }));
  const roleColor = (roleId) => roleColorHex(roleId, props.role_names);
  const visibleRoles = (props.role_names || []).filter(
    (r) => !selectedRoleIds.length || selectedRoleIds.includes(r.id)
  );

  const settings = (
    <>
      <SettingsGroup title="Roles">
        <MultiSelect
          value={selectedRoleIds}
          onChange={setSelectedRoleIds}
          options={roleOptions}
          placeholder="All roles"
        />
      </SettingsGroup>
      <SettingsGroup title="Include">
        <OptionToggle
          label="Role color dots"
          hint="A small dot beside each name, keyed in the legend"
          checked={showRoleColors}
          onChange={setShowRoleColors}
        />
        <OptionToggle
          label="Notes and events"
          checked={showEvents}
          onChange={setShowEvents}
        />
        <OptionToggle
          label="Hide days outside the month"
          checked={hideOutsideMonth}
          onChange={setHideOutsideMonth}
        />
      </SettingsGroup>
    </>
  );

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  return (
    <PrintShell
      title="Print month"
      subtitle={monthLabel}
      documentTitle={`Schedule ${monthLabel}`}
      onClose={props.onClose}
      modeSwitcher={props.modeSwitcher}
      settings={settings}
      loading={loading}
    >
      <div>
          <div className="flex items-end justify-between pb-2 mb-2 border-b-2 border-slate-900">
            <div>
              <h3 className="text-base font-semibold leading-tight">{monthLabel}</h3>
              {storeName && (
                <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                  {storeName}
                </p>
              )}
            </div>
            {showRoleColors && visibleRoles.length > 0 && (
              <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5 max-w-[60%]">
                {visibleRoles.map((r) => (
                  <span key={r.id} className="flex items-center gap-1 text-[9px] text-slate-500">
                    <span
                      className="print-accent inline-block w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: roleColor(r.id) }}
                    />
                    {r.role_name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <table className="w-full border-collapse table-fixed text-[9px] leading-tight">
            <thead>
              <tr className="border-b border-slate-400">
                {daysOfWeek.map((d) => (
                  <th
                    key={d}
                    className="text-left font-semibold uppercase tracking-wide text-[9px] text-slate-500 py-1 px-1.5 border-l border-slate-200 first:border-l-0"
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi} className="border-b border-slate-200 last:border-b-0">
                  {week.map((day) => {
                    const hidden = hideOutsideMonth && !day.isCurrentMonth;
                    return (
                      <td
                        key={day.date}
                        className="align-top h-[92px] p-1.5 border-l border-slate-200 first:border-l-0"
                      >
                        {!hidden && (
                          <>
                            <p
                              className={`text-[10px] font-semibold mb-1 ${
                                day.isCurrentMonth ? "text-slate-900" : "text-slate-400"
                              }`}
                            >
                              {day.dayNumber}
                            </p>
                            {showEvents &&
                              day.events.map((ev, i) => (
                                <div
                                  key={`e${i}`}
                                  className="print-accent border-l-2 border-violet-500 pl-1 mb-1 text-violet-900 min-w-0"
                                  title={ev.title}
                                >
                                  <p className="break-words">
                                    <span className="font-semibold">
                                      {ev.title?.trim() || "Untitled note"}
                                    </span>
                                    {ev.start_time && (
                                      <span className="text-violet-700">
                                        {" "}
                                        {get_t_str_shortened(ev.start_time)}
                                        {ev.end_time && `–${get_t_str_shortened(ev.end_time)}`}
                                      </span>
                                    )}
                                  </p>
                                  {ev.description && (
                                    <p className="break-words text-violet-700">
                                      {ev.description}
                                    </p>
                                  )}
                                </div>
                              ))}
                            {day.shifts.map((shift, i) => {
                              const isOpen = shift.status === "AVAILABLE";
                              const roleLabel =
                                props.role_names?.find((r) => r.id === shift.role_id)
                                  ?.role_name || shift.role_name;
                              const name = isOpen
                                ? `Open${roleLabel ? ` · ${roleLabel}` : ""}`
                                : `${shift.first_name || ""} ${
                                    shift.last_name ? shift.last_name[0] + "." : ""
                                  }`.trim();
                              return (
                                <p key={i} className="flex items-baseline gap-1 mb-0.5 min-w-0">
                                  {showRoleColors && (
                                    <span
                                      className="print-accent shrink-0 inline-block w-1.5 h-1.5 rounded-full relative top-[-1px]"
                                      style={{ backgroundColor: roleColor(shift.role_id) }}
                                    />
                                  )}
                                  <span className="shrink-0 tabular-nums text-slate-600">
                                    {get_t_str_shortened(shift.shift_start)}–
                                    {shift.business_decline
                                      ? "BD"
                                      : get_t_str_shortened(shift.shift_end)}
                                  </span>
                                  <span
                                    className={`truncate ${
                                      isOpen ? "italic text-slate-500" : "font-semibold"
                                    }`}
                                  >
                                    {name || "No name"}
                                  </span>
                                </p>
                              );
                            })}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </PrintShell>
  );
}
